import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { IdentitySelector } from 'src/redux/selectors/identity.selector';
import usePollingStatuses from 'src/helpers/hooks/usePollingStatuses';
import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Box, Button, Modal, TextField, Typography } from '@material-ui/core';
import certificateManagementActions from 'src/redux/actions/certificateManagement.actions';
import DocumentLoader from 'src/components/DocumentLoader';
import { useStyles } from './styles';

const RevalidateMultipleCertificates = ({ open, documentIds, onCancel, onFinish }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { workspaceId } = useSelector(IdentitySelector.selectWorkspaceDetails, undefined);
  const count = documentIds.length;
  const classes = useStyles();
  const [reason, setReason] = useState('');
  const validStatusTag = 'valid';

  const cleanUp = (callback) => {
    setReason('');
    callback();
  };

  const { isLoading, progress, completedCount, trackProgress } = usePollingStatuses({
    targetStatus: validStatusTag,
    onFinish: () => cleanUp(onFinish),
  });

  const handleChangeReason = (e) => {
    setReason(e.target.value);
  };

  const renderModal = (
    <div style={{ paddingTop: 40, paddingBottom: 40 }}>
      <Typography variant="h2" className={classes.header}>
        {String(t('documentActions.validateMultipleDocuments.header'))}
      </Typography>
      <Typography
        id="modal-modal-description"
        sx={{ mt: 2 }}
        className={classes.message}
        variant="body1"
      >
        {String(t('documentActions.validateMultipleDocuments.description', { count }))}
      </Typography>
      <TextField
        multiline
        className={classes.textArea}
        maxRows={6}
        value={reason}
        onChange={handleChangeReason}
        placeholder={t('documentActions.validateMultipleDocuments.placeholder')}
      />
      <div style={{ width: '100%' }}>
        <Button
          size="large"
          type="button"
          fullWidth
          className={classes.successButton}
          onClick={() =>
            dispatch(
              certificateManagementActions.validateCertificates({
                workspaceId,
                documentIds,
                reason,
                callback: () => trackProgress(documentIds),
              })
            )
          }
          disabled={!reason}
        >
          {t('documentActions.validateMultipleDocuments.sign')}
        </Button>
        <Button
          size="large"
          type="button"
          fullWidth
          variant="text"
          onClick={() => cleanUp(onCancel)}
        >
          {t('common.cancel')}
        </Button>
      </div>
    </div>
  );

  return (
    <>
      <Modal
        title={t('documentActions.validateMultipleDocuments.header')}
        open={open}
        onClose={() => cleanUp(onCancel)}
        aria-labelledby="modal-modal-title"
        aria-describedby="modal-modal-description"
      >
        <Box className={classes.modal}>
          {!isLoading ? (
            renderModal
          ) : (
            <DocumentLoader
              title={t('documentActions.validateMultipleDocuments.toast.waitForCountUpdate')}
              progress={progress}
              count={count}
              completedCount={completedCount}
            />
          )}
        </Box>
      </Modal>
    </>
  );
};

RevalidateMultipleCertificates.propTypes = {
  open: PropTypes.bool,
  documentIds: PropTypes.arrayOf(PropTypes.string).isRequired,
  onCancel: PropTypes.func,
  onFinish: PropTypes.bool,
};

RevalidateMultipleCertificates.defaultProps = {
  open: false,
};

export default RevalidateMultipleCertificates;
